// @flow

import type { State } from '../travelerInfo';
import type { Action } from '../../state';

export type AccompanyingPets = 'cats' | 'dogs' | 'cats_and_dogs' | 'none';

export type ActionData = {|
  start: ?Date,
  end: ?Date,
  adults: number,
  pets?: AccompanyingPets,
  kids?: number,
|};

export type VrTravelerInfo = {|
  start?: string,
  end?: string,
  adults: number,
  children?: number,
  pets?: AccompanyingPets,
|};

const SET_VR_TRAVELER_INFO = 'SET_VR_TRAVELER_INFO';

// TODO: we should go through and refine the return type of this action in extant code
export const setVrTravelerInfo = (data: ActionData) => ({
  type: SET_VR_TRAVELER_INFO,
  travelerInfo: data,
});

function formatDay(date: Date) {
  return `0${date.getDate()}`.slice(-2);
}

function formatMonth(date: Date) {
  const month = date.getMonth() + 1;
  return `0${month.toString(10)}`.slice(-2);
}

// globalize.formatDate does not have the format we need for this
export function dateToStandardString(date: Date): string {
  return `${date.getFullYear()}/${formatMonth(date)}/${formatDay(date)}`;
}

function isDifferentDate(actionDate: ?Date, startDate?: ?string): boolean {
  if (!actionDate && !startDate) return false;
  if (!startDate || !actionDate) return true;
  return actionDate.toDateString() !== new Date(startDate).toDateString();
}

/**
 * check if we should update the traveler info based on check in data param changes
 * @param state
 * @param data
 * @returns {boolean}
 */
export function shouldUpdateTravelerInfo(state: VrTravelerInfo, data: ActionData): boolean {
  const { start, end, adults, pets = 'none', kids = 0 } = data;
  const {
    adults: currentAdults,
    start: currentStart,
    end: currentEnd,
    pets: currentPets = 'none',
    children = 0,
  } = state;
  return (
    isDifferentDate(start, currentStart) ||
    isDifferentDate(end, currentEnd) ||
    adults !== currentAdults ||
    pets !== currentPets ||
    kids !== children
  );
}

function handleSetVrTravelerInfo(state: State, data: ActionData): State {
  const shouldUpdate = state.vr ? shouldUpdateTravelerInfo(state.vr, data) : true;
  if (!shouldUpdate) return state;
  const { start, end, adults, pets, kids } = data;
  const travelerInfo: VrTravelerInfo = {
    adults,
    pets,
    children: kids,
  };

  if (start) {
    travelerInfo.start = dateToStandardString(start);
  }
  if (end) {
    travelerInfo.end = dateToStandardString(end);
  }
  return { ...state, vr: travelerInfo };
}

const emptyState = () => Object.freeze({});

export default (state: State = emptyState(), action: Action): State => {
  switch (action.type) {
    case SET_VR_TRAVELER_INFO:
      return handleSetVrTravelerInfo(state, action.travelerInfo);
    default:
      return state;
  }
};
